/*
 * Copyright (c) 2026 Bolmate B.V.
 */

import {ClauseType, ProductSelectionClauseType} from './types';

import {deepClone} from './utils';

export class ProductSelectionHelper {
    clauses: Array<ProductSelectionClauseType>;

    constructor(clauses: Array<ProductSelectionClauseType>) {
        // Create a copy of the clauses to prevent in place edits
        this.clauses = deepClone(clauses);
    }

    selectProduct = (productID: string, clauseType: ClauseType): Array<ProductSelectionClauseType> => {
        this.setProductID(productID, clauseType);
        return this.clauses;
    };

    selectQuery = (query: string, clauseType: ClauseType): Array<ProductSelectionClauseType> => {
        this.setQuery(query, clauseType);
        return this.clauses;
    };

    selectAll = (clauseType: ClauseType): Array<ProductSelectionClauseType> => {
        if (clauseType === 'include') this.clauses = [{clause_type: clauseType, selection_type: 'all'}];
        else this.clauses = [];
        return this.clauses;
    };

    selectRemaining = (clauseType: ClauseType): Array<ProductSelectionClauseType> => {
        if (clauseType === 'include') this.clauses = [{clause_type: clauseType, selection_type: 'remaining'}];
        else this.clauses = [];
        return this.clauses;
    };

    isAllSelected = (): boolean => {
        return this.clauses.length === 1 && this.clauses[0].selection_type === 'all';
    };

    isRemainingSelected = (): boolean => {
        return this.clauses.length === 1 && this.clauses[0].selection_type === 'remaining';
    };

    isAnySelected = (): boolean => {
        return !this.isRemainingSelected() && this.clauses.length > 0;
    };

    private setProductID = (productID: string, clauseType: ClauseType) => {
        const removeFrom = clauseType === 'include' ? 'exclude' : 'include';
        if (this.removeProduct(productID, removeFrom)) return;
        this.addOrCreateProduct(productID, clauseType);
    };

    private setQuery = (query: string, clauseType: ClauseType) => {
        const removeFrom = clauseType === 'include' ? 'exclude' : 'include';
        if (this.removeQuery(query, removeFrom)) return;
        this.addOrCreateQuery(query, clauseType);
    };

    private removeProduct(productID: string, clauseType: ClauseType): boolean {
        const clauses = this.clauses.toReversed();
        for (let ix = 0; ix < clauses.length; ix++) {
            const clause = clauses[ix];
            if (clause.selection_type !== 'id') return false;
            if (clause.clause_type === clauseType && clause.ids.includes(productID)) {
                clause.ids.splice(clause.ids.indexOf(productID), 1);
                if (!clause.ids.length) clauses.splice(ix, 1);
                this.clauses = clauses.toReversed();
                return true;
            }
        }
        return false;
    }

    private removeQuery(query: string, clauseType: ClauseType): boolean {
        const clauses = this.clauses.toReversed();
        for (let ix = 0; ix < clauses.length; ix++) {
            const clause = clauses[ix];
            if (clause.selection_type !== 'search') return false;
            if (clause.clause_type === clauseType && clause.queries.includes(query) && clause.queries.length === 1) {
                this.clauses.splice(ix, 1).toReversed();
                return true;
            }
        }
        return false;
    }

    private addOrCreateProduct(productID: string, clauseType: ClauseType) {
        const lastClause = this.lastClause;
        if (lastClause && lastClause.selection_type === 'id' && lastClause.clause_type === clauseType) {
            lastClause.ids.push(productID);
            return;
        }
        this.clauses.push({clause_type: clauseType, selection_type: 'id', ids: [productID]});
    }

    private addOrCreateQuery(query: string, clauseType: ClauseType) {
        const lastClause = this.lastClause;
        if (lastClause && lastClause.selection_type === 'search' && lastClause.clause_type === clauseType) {
            lastClause.queries.push(query);
            return;
        }
        this.clauses.push({clause_type: clauseType, selection_type: 'search', queries: [query]});
    }

    private get lastClause(): ProductSelectionClauseType | null {
        if (!this.clauses.length) return null;
        return this.clauses[this.clauses.length - 1];
    }
}
